/**
 * Supplier Pricing & Procurement hub (C5).
 *
 * Pushed OVER the tabs on the parent (app) stack (title "Pricing", with back).
 *
 * Section 1 "Your connections"        — GET /api/supplier-connections, status badges,
 *                                        tap → /(app)/supplier-pricing/[id].
 * Section 2 "Price imports to review" — GET /api/price-imports?status=pending, with
 *                                        Confirm / Reject buttons per row.
 * "+ Connect a price source" button   → push /(app)/supplier-pricing/connect.
 *
 * Connection statuses come back snake_case from raw Prisma rows; mapped to Badge keys
 * locally (statusMap is a shared file we must not modify). NEVER imports @smartbox/*.
 * No raw style values.
 */

import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listConnections,
  listPriceImports,
  confirmPriceImport,
  rejectPriceImport,
  mmiStatus,
  mmiSync,
  type ConnectionRow,
  type ConnectionStatus,
  type PriceImportRow,
} from '../../src/api/pricing';
import { Screen, Text, Card, Button, Badge, ListRow } from '../../src/components/ui';
import { theme, type StatusKey } from '../../src/theme';
import { extractErrorMessage, humanize, formatDateTime } from '../../src/lib/format';

// ---------------------------------------------------------------------------
// Local status → Badge mapping (statusMap.ts is shared; not modified here).
// ---------------------------------------------------------------------------

function connectionBadge(status: ConnectionStatus): { key: StatusKey; label: string } {
  switch (status) {
    case 'linked':
      return { key: 'paid', label: 'Linked' };
    case 'pending':
      return { key: 'inProgress', label: 'Pending' };
    case 'error':
      return { key: 'overdue', label: 'Error' };
    case 'expired':
      return { key: 'overdue', label: 'Expired' };
    default:
      return { key: 'draft', label: 'Unlinked' };
  }
}

export default function PricingHubScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const connectionsQuery = useQuery({
    queryKey: ['supplier-connections'],
    queryFn: () => listConnections(),
    staleTime: 30_000,
  });

  const importsQuery = useQuery({
    queryKey: ['price-imports', 'pending'],
    queryFn: () => listPriceImports({ status: 'pending' }),
    staleTime: 30_000,
  });

  const confirmMutation = useMutation({
    mutationFn: (importId: string) => confirmPriceImport(importId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['price-imports'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (importId: string) => rejectPriceImport(importId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['price-imports'] });
    },
  });

  const mmiQuery = useQuery({
    queryKey: ['mmi-status'],
    queryFn: mmiStatus,
    staleTime: 30_000,
  });

  const mmiSyncMutation = useMutation({
    mutationFn: () => mmiSync(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mmi-status'] });
    },
  });

  const connections = connectionsQuery.data ?? [];
  const imports = importsQuery.data ?? [];
  const mmi = mmiQuery.data;

  return (
    <Screen scroll>
      <Text variant="title">Pricing & procurement</Text>

      {/* Market pricing (MMI) — the always-on market source that prices estimate/bid drafts. */}
      <Card>
        <View style={styles.importHead}>
          <Text variant="bodyStrong">Market pricing (MMI)</Text>
          <Badge
            status={mmi?.configured && mmi.health.ok ? 'paid' : mmi?.configured ? 'overdue' : 'draft'}
            label={mmi?.configured ? (mmi.health.ok ? 'Live' : 'Unhealthy') : 'Not set up'}
          />
        </View>
        <Text variant="small" color="secondary">
          Current market prices used to price estimates and bids wherever you have no negotiated
          supplier cost. {mmi?.configured
            ? `${mmi.reference_prices} material${mmi.reference_prices === 1 ? '' : 's'} priced${mmi.last_sync_at ? ` · last refreshed ${formatDateTime(mmi.last_sync_at)}` : ''}.`
            : 'Set the MMI API key on the server to turn this on.'}
        </Text>
        {mmiSyncMutation.data && !mmiSyncMutation.isPending ? (
          <Text variant="small" color={mmiSyncMutation.data.enabled ? 'secondary' : 'danger'}>
            {mmiSyncMutation.data.enabled
              ? `Refreshed: ${mmiSyncMutation.data.updated} of ${mmiSyncMutation.data.materialsConsidered} materials priced (${mmiSyncMutation.data.skipped} unmatched).`
              : (mmiSyncMutation.data.message ?? 'Market pricing is not configured.')}
          </Text>
        ) : null}
        {mmiSyncMutation.isError ? (
          <Text variant="small" color="danger" accessibilityRole="alert">
            {extractErrorMessage(mmiSyncMutation.error, 'Could not refresh market pricing.')}
          </Text>
        ) : null}
        <Button
          title="Refresh market pricing"
          variant="secondary"
          size="sm"
          fullWidth={false}
          loading={mmiSyncMutation.isPending}
          disabled={mmiSyncMutation.isPending || mmi?.configured === false}
          onPress={() => mmiSyncMutation.mutate()}
          accessibilityLabel="Pull the latest market prices into your catalog"
        />
      </Card>

      <Button
        title="+ Connect a price source"
        onPress={() => router.push('/(app)/supplier-pricing/connect')}
        accessibilityLabel="Connect a price source"
      />

      {/* Section 1 — Your connections */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Your connections
        </Text>
        {connectionsQuery.isLoading ? (
          <View style={styles.inlineState}>
            <ActivityIndicator
              color={theme.color.action.primary}
              accessibilityLabel="Loading connections"
            />
          </View>
        ) : connectionsQuery.isError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(connectionsQuery.error, 'Failed to load connections.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => connectionsQuery.refetch()}
            />
          </Card>
        ) : connections.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No supplier connections yet. Connect a price source to pull live pricing.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {connections.map((c: ConnectionRow) => {
              const b = connectionBadge(c.status);
              const title = c.display_name ?? c.connector_code ?? c.id.slice(0, 8).toUpperCase();
              const subtitle = c.account_number
                ? `Acct ${c.account_number}`
                : c.last_synced_at
                  ? `Synced ${formatDateTime(c.last_synced_at)}`
                  : humanize(c.status);
              return (
                <ListRow
                  key={c.id}
                  title={title}
                  subtitle={subtitle}
                  right={<Badge status={b.key} label={b.label} />}
                  onPress={() => router.push(`/(app)/supplier-pricing/${c.id}`)}
                />
              );
            })}
          </View>
        )}
      </View>

      {/* Section 2 — Price imports to review */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Price imports to review
        </Text>
        {importsQuery.isLoading ? (
          <View style={styles.inlineState}>
            <ActivityIndicator
              color={theme.color.action.primary}
              accessibilityLabel="Loading price imports"
            />
          </View>
        ) : importsQuery.isError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(importsQuery.error, 'Failed to load price imports.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => importsQuery.refetch()}
            />
          </Card>
        ) : imports.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              Nothing to review. New price imports awaiting confirmation will appear here.
            </Text>
          </Card>
        ) : (
          <View style={styles.list}>
            {imports.map((imp: PriceImportRow) => {
              const busy = confirmMutation.isPending || rejectMutation.isPending;
              const lines = imp.line_count ?? 0;
              return (
                <Card key={imp.id}>
                  <View style={styles.importHead}>
                    <View style={styles.importMeta}>
                      <Text variant="bodyStrong">
                        Import {imp.id.slice(0, 8).toUpperCase()}
                      </Text>
                      <Text variant="small" color="secondary">
                        {lines} {lines === 1 ? 'line' : 'lines'}
                        {imp.tier != null ? ` · Tier ${imp.tier}` : ''}
                        {imp.created_at ? ` · ${formatDateTime(imp.created_at)}` : ''}
                      </Text>
                    </View>
                    <Badge status="inProgress" label="Pending" />
                  </View>
                  <View style={styles.importActions}>
                    <Button
                      title="View status"
                      variant="secondary"
                      size="sm"
                      onPress={() => router.push(`/(app)/supplier-pricing/import/${imp.id}`)}
                      accessibilityLabel={`View status of import ${imp.id.slice(0, 8)}`}
                    />
                    <Button
                      title="Confirm"
                      size="sm"
                      loading={confirmMutation.isPending && confirmMutation.variables === imp.id}
                      disabled={busy}
                      onPress={() => confirmMutation.mutate(imp.id)}
                      accessibilityLabel={`Confirm import ${imp.id.slice(0, 8)}`}
                    />
                    <Button
                      title="Reject"
                      variant="danger"
                      size="sm"
                      loading={rejectMutation.isPending && rejectMutation.variables === imp.id}
                      disabled={busy}
                      onPress={() => rejectMutation.mutate(imp.id)}
                      accessibilityLabel={`Reject import ${imp.id.slice(0, 8)}`}
                    />
                  </View>
                </Card>
              );
            })}
          </View>
        )}
        {confirmMutation.isError || rejectMutation.isError ? (
          <Card>
            <Text variant="body" color="danger" accessibilityRole="alert">
              {extractErrorMessage(
                confirmMutation.error ?? rejectMutation.error,
                'Could not update the import.',
              )}
            </Text>
          </Card>
        ) : null}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  section: { gap: theme.spacing.sm },
  list: { gap: theme.spacing.sm },
  inlineState: { paddingVertical: theme.spacing.lg, alignItems: 'center' },
  importHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  importMeta: { flex: 1, gap: 2 },
  importActions: { flexDirection: 'row', gap: theme.spacing.sm },
});
